import React, { useState, useEffect, useMemo } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';

import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaTags, FaSearch, FaSort, FaSortUp, FaSortDown, FaFilter } from 'react-icons/fa';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import axios from 'axios';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacySubCategory = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        setTitle('الفئات الفرعية');
        fetchSubCategories();
    }, [setTitle]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchSubCategories = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error('لا يوجد token للمصادقة');
            }

            const response = await axios.get(`${BASE_URL}/pharmacy/sub-categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 'failed' || response.data.status === 'faild') {
                throw new Error(response.data.message || 'فشل في جلب الفئات الفرعية');
            }

            setSubCategories(response.data.data || []);
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'فشل في جلب الفئات الفرعية');
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب الفئات الفرعية');
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = async (categoryId, categoryName) => {
        setConfirmModal({
            isOpen: true,
            data: { categoryId, categoryName },
            message: `هل أنت متأكد من حذف الفئة الفرعية "${categoryName}"؟`,
            title: 'تأكيد الحذف'
        });
    };

    const executeDelete = async () => {
        try {
            const token = getAuthToken();
            await axios.delete(`${BASE_URL}/pharmacy/sub-categories/${confirmModal.data.categoryId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            showSuccessToast('تم حذف الفئة الفرعية بنجاح');
            fetchSubCategories();
        } catch (error) {
            showErrorToast('فشل في حذف الفئة الفرعية');
        }
    };

    // Column helper for table configuration
    const columnHelper = createColumnHelper();

    // Table columns configuration
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: '#',
                cell: ({ row }) => (
                    <span className="fw-semibold text-muted">#{row.index + 1}</span>
                ),
                size: 60,
            }),
            columnHelper.accessor('image', {
                id: 'image',
                header: 'الصورة',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        {getValue() ? (
                            <img
                                src={getValue()}
                                alt="صورة الفئة"
                                style={{
                                    width: '50px',
                                    height: '50px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '2px solid #e9ecef'
                                }}
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/50?text=No+Image';
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: '50px',
                                    height: '50px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid #e9ecef',
                                    margin: '0 auto'
                                }}
                            >
                                <span style={{ fontSize: '10px', color: '#6c757d' }}>لا توجد صورة</span>
                            </div>
                        )}
                    </div>
                ),
                size: 100,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'اسم الفئة الفرعية',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <h6 className="mb-0 fw-semibold" style={{ fontSize: '12px' }}>
                            {getValue() || 'غير محدد'}
                        </h6>
                    </div>
                ),
                size: 150,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const category = row.original;
                    return (
                        <div className="action-buttons d-flex gap-3 flex-wrap justify-content-center">
                            <button
                                className="btn btn-sm btn-action"
                                style={{
                                    backgroundColor: '#184194',
                                    borderColor: '#184194',
                                    color: 'white',
                                    fontSize: '12px',
                                    padding: '6px 12px',
                                    borderRadius: '8px'
                                }}
                                title="تعديل"
                                onClick={() => navigate(`/pharmacy-subcategories/edit/${category.id}`)}
                            >
                                <FaEdit className="mx-1" />
                                تعديل
                            </button>
                            <button
                                className="btn btn-sm btn-action"
                                style={{
                                    backgroundColor: '#dc3545',
                                    borderColor: '#dc3545',
                                    color: 'white',
                                    fontSize: '12px',
                                    padding: '6px 12px',
                                    borderRadius: '8px'
                                }}
                                title="حذف"
                                onClick={() => handleDelete(category.id, category.name)}
                            >
                                <FaTrash className="mx-1" />
                                حذف
                            </button>
                        </div>
                    );
                },
                size: 150,
            }),
        ],
        [navigate]
    );

    // Table configuration
    const table = useReactTable({
        data: subCategories || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const LoadingSpinner = () => (
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <LoadingSpinner />
                        <p className="mt-3 text-muted">جاري تحميل الفئات الفرعية...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="alert alert-danger">
                            <h4>خطأ</h4>
                            <p>{error}</p>
                            <button 
                                className="btn btn-primary"
                                onClick={() => window.location.reload()}
                            >
                                إعادة المحاولة
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            

            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0" style={{ fontSize: '20px' }}>الفئات الفرعية</h2>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-primary d-flex align-items-center"
                        style={{
                            backgroundColor: '#184194',
                            border: 'none',
                            borderRadius: '8px'
                        }}
                        onClick={() => navigate('/pharmacy-subcategories/add')}>
                        <FaPlus className="mx-2" />
                        إضافة فئة فرعية جديدة
                    </motion.button>
                </div>


                {/* Search and Filter Controls */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern mb-4"
                >
                    <div className="card-body p-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="search-container position-relative">
                                    <div className="input-group search-input-group">
                                        <span className="input-group-text search-icon">
                                            <FaSearch className="text-white" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control search-input"
                                            placeholder="البحث في الفئات الفرعية..."
                                            value={globalFilter ?? ''}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                                    {(globalFilter || columnFilters.length > 0) && (
                                        <span className="badge bg-info text-white mx-2">
                                            {table.getFilteredRowModel().rows.length} من {subCategories?.length || 0} نتيجة
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FaFilter className="mx-1" />
                                        {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setGlobalFilter('');
                                            setColumnFilters([]);
                                            setSorting([]);
                                        }}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        مسح الكل
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-top"
                            >
                                <div className="row">
                                    <div className="col-md-12">
                                        <p className="text-muted mb-0">لا توجد مرشحات متاحة حالياً</p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Modern Data Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern"
                >
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">جاري التحميل...</span>
                                </div>
                            </div>
                        ) : !Array.isArray(subCategories) || subCategories.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <FaTags size={48} className="mb-3" />
                                    <p>لا توجد فئات فرعية في الوقت الحالي</p>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table mb-0 table-modern" style={{
                                    backgroundColor: 'transparent', 
                                    '--bs-table-striped-bg': 'transparent',
                                    '--bs-table-hover-bg': 'rgba(0,0,0,0.05)'
                                }}>
                                    <thead className="table-light">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th
                                                        key={header.id}
                                                        className="border-0 fw-semibold text-center"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                            width: header.getSize()
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getCanSort() && (
                                                                <span>
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <FaSortUp className="text-primary" />
                                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                                        <FaSortDown className="text-primary" />
                                                                    ) : (
                                                                        <FaSort className="text-muted" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                        </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <motion.tr
                                                key={row.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                style={{ backgroundColor: 'transparent' }}
                                                className="table-row-hover"
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="border-0 text-center align-middle"
                                                            style={{ 
                                                            backgroundColor: 'transparent',
                                                            width: cell.column.getSize()
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                                ))}
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Table Pagination */}
                    {table.getPageCount() > 1 && (
                        <div className="card-footer bg-light">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="text-muted">عرض</span>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: 'auto' }}
                                            value={table.getState().pagination.pageSize}
                                            onChange={(e) => table.setPageSize(Number(e.target.value))}
                                        >
                                            {[5, 10, 20, 30, 50].map(pageSize => (
                                                <option key={pageSize} value={pageSize}>
                                                    {pageSize}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="text-muted">
                                            من {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} إلى{' '}
                                            {Math.min(
                                                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                                table.getFilteredRowModel().rows.length
                                            )}{' '}
                                            من أصل {table.getFilteredRowModel().rows.length} نتيجة
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-end gap-2 flex-wrap">
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            الأولى
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            السابقة
                                        </button>
                                        <span className="d-flex align-items-center px-3">
                                            صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                        </span>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            التالية
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            الأخيرة
                                        </button>
                            </div>
                        </div>
                        </div>
                    </div>
                )}
                </motion.div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, data: null })}
                onConfirm={executeDelete}
                title="تأكيد الحذف"
                message={confirmModal.data?.message || 'هل أنت متأكد من هذا الإجراء؟'}
                confirmText="حذف"
                cancelText="إلغاء"
                confirmButtonColor="#dc3545"
                type="danger"
            />
        </div>
    );
};

export default PharmacySubCategory;

// CSS للجدول المحسن
const styles = `
.table-modern {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 600px;
}

.table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 1rem 0.75rem;
    vertical-align: middle;
}

.table-modern tbody tr {
    border-bottom: 1px solid #f1f3f4;
    transition: all 0.2s ease;
}

.table-modern tbody tr:last-child {
    border-bottom: none;
}

.table-modern td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    white-space: normal;
    word-wrap: break-word;
    max-width: 200px;
    min-width: 120px;
}

/* تحسين عرض اسم الفئة الفرعية */
.table-modern td:nth-child(2) {
    max-width: 150px;
    min-width: 100px;
    text-align: center;
}

.table-modern td:nth-child(2) h6 {
    font-size: 12px;
    line-height: 1.3;
    margin: 0;
}

.action-buttons .btn {
    margin: 2px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s ease;
    border: none;
    min-width: 80px;
}

.action-buttons .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.card-modern {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.card-modern:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.card-modern .card-body {
    padding: 1.5rem;
}

.table-responsive {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
}

/* Custom Horizontal Scrollbar for Table */
.table-responsive::-webkit-scrollbar {
    height: 8px;
    width: 0;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 4px;
    transition: all 0.3s ease;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    transform: scale(1.1);
}

/* Firefox Horizontal Scrollbar */
.table-responsive {
    scrollbar-width: thin;
    scrollbar-color: #184194 #f1f1f1;
}

/* Search Input Styling */
.search-container {
    position: relative;
}

.search-input-group {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.search-input-group:focus-within {
    box-shadow: 0 4px 8px rgba(24, 65, 148, 0.2);
    transform: translateY(-1px);
}

.search-icon {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: none;
    color: white;
    padding: 0.75rem 1rem;
}

.search-input {
    border: none;
    padding: 0.75rem 1rem;
    font-size: 14px;
    background: #fff;
}

.search-input:focus {
    box-shadow: none;
    border: none;
    outline: none;
}

.btn-outline-secondary {
    border-color: #6c757d;
    color: #6c757d;
    transition: all 0.2s ease;
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
    color: white;
    transform: translateY(-1px);
}

.btn-outline-primary {
    border-color: #184194;
    color: #184194;
    transition: all 0.2s ease;
}

.btn-outline-primary:hover {
    background-color: #184194;
    border-color: #184194;
}

.table-row-hover:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
}

@media (max-width: 768px) {
    .action-buttons {
        flex-direction: column;
        gap: 4px;
    }
    
    .action-buttons .btn {
        width: 100%;
        font-size: 11px;
        padding: 4px 6px;
    }
    
    .table-modern {
        min-width: 500px;
    }
    
    .table-modern th,
    .table-modern td {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
        max-width: 150px;
        min-width: 80px;
    }
    
    /* تحسين عرض اسم الفئة الفرعية للشاشات الصغيرة */
    .table-modern td:nth-child(2) {
        max-width: 120px;
        min-width: 80px;
    }
    
    .table-modern td:nth-child(2) h6 {
        font-size: 11px;
        line-height: 1.2;
    }
    
    .search-input-group {
        margin-bottom: 1rem;
    }
    
    .search-input {
        font-size: 16px; /* منع التكبير في iOS */
    }
}
`;

// إضافة الـ CSS للصفحة
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
